import { Card, CardActionArea, CardContent, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export type ActionTone = 'critical' | 'warning' | 'info' | 'neutral';

const TONE: Record<ActionTone, { fg: string; bg: string }> = {
  critical: { fg: 'error.main', bg: 'rgba(211, 47, 47, 0.08)' },
  warning: { fg: 'warning.dark', bg: 'rgba(237, 108, 2, 0.10)' },
  info: { fg: 'primary.main', bg: 'rgba(37, 99, 235, 0.10)' },
  neutral: { fg: 'text.primary', bg: 'transparent' },
};

interface Props {
  label: string;
  /** 큰 숫자 */
  value: number | string;
  /** 숫자 뒤 보조 표기 (예: "/ 7명", "%") */
  suffix?: string;
  hint?: string;
  tone?: ActionTone;
  /** 클릭 시 이동할 경로. 없으면 클릭 불가 */
  to?: string;
}

/**
 * 대시보드 지표 카드.
 *
 * 값이 0이면 조치할 게 없다는 뜻이므로 강조 색을 빼고 회색으로 낮춘다.
 * 모든 카드가 항상 빨갛게 보이면 진짜 급한 것이 묻힌다.
 */
export function ActionCard({ label, value, suffix, hint, tone = 'neutral', to }: Props) {
  const navigate = useNavigate();
  const isZero = value === 0;
  const effective = isZero ? 'neutral' : tone;
  const { fg, bg } = TONE[effective];

  const body = (
    <CardContent sx={{ bgcolor: bg, height: '100%' }}>
      <Stack spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.5}>
          <Typography variant="h4" fontWeight={700} color={isZero ? 'text.disabled' : fg}>
            {value}
          </Typography>
          {suffix && (
            <Typography variant="body2" color="text.secondary">
              {suffix}
            </Typography>
          )}
        </Stack>
        <Typography variant="caption" color="text.disabled">
          {hint ?? ' '}
        </Typography>
      </Stack>
    </CardContent>
  );

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      {to ? (
        <CardActionArea
          onClick={() => navigate(to)}
          sx={{ height: '100%', alignItems: 'stretch' }}
          aria-label={`${label} 상세 보기`}
        >
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  );
}
